import { supabase } from '../lib/supabase';

export const credentialingService = {
  // Get all provider credentials
  async getProviderCredentials(providerId = null) {
    let query = supabase?.from('provider_credentials')?.select(`
        *,
        provider:provider_id(id, full_name, email)
      `)?.order('expiration_date', { ascending: true });

    if (providerId) {
      query = query?.eq('provider_id', providerId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      providerId: row?.provider_id,
      credentialType: row?.credential_type,
      credentialNumber: row?.credential_number,
      issuingAuthority: row?.issuing_authority,
      issueDate: row?.issue_date,
      expirationDate: row?.expiration_date,
      status: row?.status,
      specialty: row?.specialty,
      state: row?.state,
      verificationStatus: row?.verification_status,
      notes: row?.notes,
      provider: row?.provider,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  // Get insurance credentialing
  async getInsuranceCredentialing(providerId = null) {
    let query = supabase?.from('insurance_credentialing')?.select(`
        *,
        provider:provider_id(id, full_name)
      `)?.order('application_date', { ascending: false });

    if (providerId) {
      query = query?.eq('provider_id', providerId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      providerId: row?.provider_id,
      insuranceNetwork: row?.insurance_network,
      applicationDate: row?.application_date,
      status: row?.status,
      approvalDate: row?.approval_date,
      expirationDate: row?.expiration_date,
      providerNumber: row?.provider_number,
      reimbursementRate: row?.reimbursement_rate,
      directoryUpdated: row?.directory_updated,
      contactPerson: row?.contact_person,
      contactEmail: row?.contact_email,
      contactPhone: row?.contact_phone,
      notes: row?.notes,
      provider: row?.provider,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  // Get compliance documents
  async getComplianceDocuments(providerId = null, credentialId = null) {
    let query = supabase?.from('compliance_documents')?.select(`
        *,
        provider:provider_id(id, full_name),
        uploaded_by_user:uploaded_by(id, full_name)
      `)?.order('upload_date', { ascending: false });

    if (providerId) {
      query = query?.eq('provider_id', providerId);
    }
    if (credentialId) {
      query = query?.eq('credential_id', credentialId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      providerId: row?.provider_id,
      credentialId: row?.credential_id,
      documentType: row?.document_type,
      documentName: row?.document_name,
      fileUrl: row?.file_url,
      fileSize: row?.file_size,
      version: row?.version,
      uploadDate: row?.upload_date,
      uploadedBy: row?.uploaded_by,
      expirationDate: row?.expiration_date,
      verificationStatus: row?.verification_status,
      notes: row?.notes,
      provider: row?.provider,
      uploadedByUser: row?.uploaded_by_user,
      createdAt: row?.created_at
    }));
  },

  // Get renewal reminders
  async getRenewalReminders(providerId = null) {
    let query = supabase?.from('credential_renewal_reminders')?.select(`
        *,
        credential:credential_id(credential_type, credential_number, expiration_date),
        provider:provider_id(id, full_name)
      `)?.order('scheduled_date', { ascending: true });

    if (providerId) {
      query = query?.eq('provider_id', providerId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      credentialId: row?.credential_id,
      providerId: row?.provider_id,
      reminderType: row?.reminder_type,
      scheduledDate: row?.scheduled_date,
      notificationChannel: row?.notification_channel,
      status: row?.status,
      sentAt: row?.sent_at,
      messageContent: row?.message_content,
      credential: row?.credential,
      provider: row?.provider,
      createdAt: row?.created_at
    }));
  },

  // Create new credential
  async createCredential(credentialData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('provider_credentials')?.insert({
        provider_id: user?.id,
        credential_type: credentialData?.credentialType,
        credential_number: credentialData?.credentialNumber,
        issuing_authority: credentialData?.issuingAuthority,
        issue_date: credentialData?.issueDate,
        expiration_date: credentialData?.expirationDate,
        specialty: credentialData?.specialty,
        state: credentialData?.state,
        notes: credentialData?.notes
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Update credential
  async updateCredential(credentialId, updates) {
    const { data, error } = await supabase?.from('provider_credentials')?.update({
        credential_number: updates?.credentialNumber,
        issuing_authority: updates?.issuingAuthority,
        issue_date: updates?.issueDate,
        expiration_date: updates?.expirationDate,
        specialty: updates?.specialty,
        state: updates?.state,
        notes: updates?.notes,
        updated_at: new Date()?.toISOString()
      })?.eq('id', credentialId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Create insurance credentialing
  async createInsuranceCredentialing(credentialingData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('insurance_credentialing')?.insert({
        provider_id: user?.id,
        insurance_network: credentialingData?.insuranceNetwork,
        application_date: credentialingData?.applicationDate,
        status: credentialingData?.status,
        contact_person: credentialingData?.contactPerson,
        contact_email: credentialingData?.contactEmail,
        contact_phone: credentialingData?.contactPhone,
        notes: credentialingData?.notes
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Update insurance credentialing
  async updateInsuranceCredentialing(credentialingId, updates) {
    const { data, error } = await supabase?.from('insurance_credentialing')?.update({
        status: updates?.status,
        approval_date: updates?.approvalDate,
        expiration_date: updates?.expirationDate,
        provider_number: updates?.providerNumber,
        reimbursement_rate: updates?.reimbursementRate,
        directory_updated: updates?.directoryUpdated,
        notes: updates?.notes,
        updated_at: new Date()?.toISOString()
      })?.eq('id', credentialingId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Get credentialing statistics
  async getCredentialingStats(providerId = null) {
    const credentials = await this.getProviderCredentials(providerId);
    const insuranceCredentialing = await this.getInsuranceCredentialing(providerId);
    const documents = await this.getComplianceDocuments(providerId);
    const reminders = await this.getRenewalReminders(providerId);

    return {
      totalCredentials: credentials?.length || 0,
      activeCredentials: credentials?.filter(c => c?.status === 'active')?.length || 0,
      expiringSoon: credentials?.filter(c => c?.status === 'expiring_soon')?.length || 0,
      expired: credentials?.filter(c => c?.status === 'expired')?.length || 0,
      insuranceNetworks: insuranceCredentialing?.length || 0,
      approvedNetworks: insuranceCredentialing?.filter(i => i?.status === 'approved')?.length || 0,
      pendingNetworks: insuranceCredentialing?.filter(i => i?.status === 'in_progress' || i?.status === 'submitted')?.length || 0,
      totalDocuments: documents?.length || 0,
      upcomingReminders: reminders?.filter(r => r?.status === 'scheduled')?.length || 0
    };
  }
};